import { useApi } from '@src/app/context/Api';
import { useCurrentUser } from '@src/entities/User/ui/hooks/useCurrentUser';
import { isEmpty, isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

/**
 * useSearch
 *
 * Retrieves all the necessary data to populate the widgets on the Risk Dashboard.
 *
 * @returns {{data, handleSearch: function({}=): void, isSearching: boolean}}
 */
export const useSearch = () => {
  const { useApiQueries } = useApi();
  const [theQueries, setTheQueries] = useState([]);
  const queries = useApiQueries(theQueries);

  const { hasPermission } = useCurrentUser();

  const isSearching = useMemo(() => {
    if (theQueries.length === 0 || isEmpty(queries)) return false;
    return (
      Object.keys(queries).length !== theQueries.length ||
      !isEmpty(
        Object.keys(queries).filter((queryKey) => {
          const query = queries[queryKey];
          return query?.isLoading || query?.isFetching;
        })
      )
    );
  }, [queries, theQueries]);

  const handleSearch = useCallback(
    (payload = {}) => {
      const requests = [
        {
          apiKey: 'RiskRecord',
          options: {
            excludedHttpCodes: [404],
          },
          payload,
          queryKey: 'GetRiskRecord',
        },
        {
          apiKey: 'RiskRecord',
          options: {
            excludedHttpCodes: [404],
          },
          payload,
          queryKey: 'GetIreRiskRecord',
        },
      ].filter((request) => {
        // Must have IRE Access
        if (request.queryKey === 'GetIreRiskRecord') {
          return hasPermission('IRE', 'access');
        }

        return true;
      });

      if (!isEqual(requests, theQueries)) {
        setTheQueries(requests);
      }
    },
    [theQueries]
  );

  return useMemo(() => {
    return { handleSearch, isSearching, searchResults: queries || {} };
  }, [isSearching, queries, handleSearch]);
};
